import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledInput } from '~/components/elements/StyledInput'
import { VStack } from '~/components/elements/VStack'
import { useFormField } from '~/hooks/useFormField'
import { useLogEvent } from '~/hooks/useLogEvent'
import { ensureExists, exists } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useCreateBrandAccountMutation } from '../hooks/useCreateBrandAccountMutation'
import { useUpdateBrandAccountMutation } from '../hooks/useUpdateBrandAccountMutation'
import { isLessThan } from '../utils'

const schema = z.object({
  title: z.string().min(1),
  partnerSeats: z.coerce.number().min(0),
  userSeats: z.coerce.number().min(0),
})
type InputValues = z.infer<typeof schema>

export const EditBrandAccountDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const { brandAccount, dialogType } = ensureExists(
    dialogInfo,
    t('assertion_failed'),
  )

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm<InputValues>({
    defaultValues: {
      title: brandAccount?.name ?? '',
      partnerSeats: brandAccount?.partnerSeat.total ?? 0,
      userSeats: brandAccount?.userSeat.total ?? 0,
    },
    resolver: zodResolver(schema),
  })
  const titleField = useFormField(control, 'title')
  const partnerSeatsField = useFormField(control, 'partnerSeats')
  const userSeatsField = useFormField(control, 'userSeats')

  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()
  const createBrandAccountMutation = useCreateBrandAccountMutation({
    onSuccess: (data, variables) => {
      // 実行者のcoreMember紐付けが見つからないとcreateBrandAccountはnullを返す。
      // 作成できていないので記録しない。作成時は id が未確定のため、送信した名前を使う。
      if (exists(data.createBrandAccount)) {
        logEvent('Create brand account', {
          eventProperties: [{ key: 'name', value: variables.name }],
        })
      }
      closeDialogWithRefresh()
    },
  })
  const updateBrandAccountMutation = useUpdateBrandAccountMutation({
    onSuccess: (data, variables) => {
      // 対象が既に削除されている場合などupdateBrandAccountはnullを返す。更新できていないので記録しない。
      if (exists(data.updateBrandAccount)) {
        logEvent('Edit brand account', {
          eventProperties: [
            { key: 'brand account id', value: variables.id },
            { key: 'name', value: variables.name },
          ],
        })
      }
      closeDialogWithRefresh()
    },
  })
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ partnerSeats, title, userSeats }) => {
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      if (dialogType === 'create') {
        createBrandAccountMutation.mutate({
          name: title,
          totalPartnerSeat: partnerSeats,
          totalUserSeat: userSeats,
        })
      } else {
        const currentPartnerSeatUsed = brandAccount?.partnerSeat.used ?? 0
        const currentUserSeatUsed = brandAccount?.userSeat.used ?? 0

        if (
          isLessThan(partnerSeats, currentPartnerSeatUsed) ||
          isLessThan(userSeats, currentUserSeatUsed)
        ) {
          const isConfirmed = window.confirm(
            t(
              'you_are_trying_to_set_fewer_seats_than_currently_registered_registered_users_will_be_deleted_to_meet_the_seat_limit',
            ),
          )
          if (isConfirmed) {
            resetDialogInfo()
          } else {
            return
          }
        }

        updateBrandAccountMutation.mutate({
          id: brandAccount.id,
          name: title,
          totalPartnerSeat: partnerSeats,
          totalUserSeat: userSeats,
        })
      }
    },
    [
      brandAccount,
      createBrandAccountMutation,
      dialogType,
      isDirty,
      resetDialogInfo,
      updateBrandAccountMutation,
      t,
    ],
  )

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={
        createBrandAccountMutation.isPending ||
        updateBrandAccountMutation.isPending
      }
      mode={dialogType === 'create' ? 'create' : 'update'}
      title={
        dialogType === 'create'
          ? t('add_brand_account')
          : t('update_brand_account')
      }
      onCancel={resetDialogInfo}
      onExecute={handleSubmit(handleValid)}
    >
      <VStack spacing={2}>
        <LabelingContainer label={t('brand_account_name')}>
          <StyledInput autoFocus={true} width="100%" {...titleField} />
        </LabelingContainer>

        <LabelingContainer label={t('partner_seats')}>
          <StyledInput type="number" width="100%" {...partnerSeatsField} />
        </LabelingContainer>

        <LabelingContainer label={t('user_seats')}>
          <StyledInput type="number" width="100%" {...userSeatsField} />
        </LabelingContainer>
      </VStack>
    </ExecuteCancelDialog>
  )
}
